import { http } from '../../http';
import { __ } from '@/js/stores/expressions';
import { useCustomConfig } from '@/js/composables/useCustomAxiosRequestConfig';
import type {
    ResponseGetRoles,
    ResponseGetRole,
    ResponseCreateRole,
    ResponseGetRolePermissions,
    RequestCreateRole,
    RequestUpdateRole,
} from './types';

// Roles CRUD

export const getRoles = async (): Promise<ResponseGetRoles> => {
    return http
        .get<ResponseGetRoles>(
            '/roles',
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-roles'),
                    502: __('error-load-roles'),
                },
            })
        )
        .then((response) => response.data);
};

export const getRole = async (roleId: string): Promise<ResponseGetRole> => {
    return http
        .get<ResponseGetRole>(
            `/roles/${roleId}`,
            useCustomConfig({
                errorMessage: {
                    404: __('error-load-role'),
                    500: __('error-load-role'),
                    502: __('error-load-role'),
                },
            })
        )
        .then((response) => response.data);
};

export const createRole = async (request: RequestCreateRole): Promise<ResponseCreateRole> => {
    return http
        .post<ResponseCreateRole>(
            '/roles',
            request,
            useCustomConfig({
                errorMessage: {
                    400: __('error-create-role'),
                    500: __('error-create-role'),
                    502: __('error-create-role'),
                },
            })
        )
        .then((response) => response.data);
};

export const updateRole = async (roleId: string, request: RequestUpdateRole): Promise<void> => {
    return http
        .put(
            `/roles/${roleId}`,
            request,
            useCustomConfig({
                errorMessage: {
                    500: __('error-update-role'),
                    502: __('error-update-role'),
                },
            })
        )
        .then(() => undefined);
};

export const deleteRole = async (roleId: string): Promise<void> => {
    return http
        .delete(
            `/roles/${roleId}`,
            useCustomConfig({
                errorMessage: {
                    500: __('error-delete-role'),
                    502: __('error-delete-role'),
                },
            })
        )
        .then(() => undefined);
};

export const updateRolePermissions = async (
    roleId: string,
    rolePermissions: Record<string, number>
): Promise<void> => {
    return http
        .put(
            `/roles/${roleId}/permissions`,
            { rolePermissions },
            useCustomConfig({
                errorMessage: {
                    500: __('error-update-role'),
                    502: __('error-update-role'),
                },
            })
        )
        .then(() => undefined);
};

export const getRolePermissions = async (roleId: string): Promise<ResponseGetRolePermissions> => {
    return http
        .get<ResponseGetRolePermissions>(
            `/roles/${roleId}/permissions`,
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-role'),
                    502: __('error-load-role'),
                },
            })
        )
        .then((response) => ({
            ...response.data,
            // Backend returns bitwise values as strings; coerce to number so that
            // strict equality checks (e.g. change detection) compare like-for-like.
            rolePermissions: Object.fromEntries(
                Object.entries(response.data.rolePermissions).map(([key, value]) => [
                    key,
                    Number(value),
                ])
            ),
        }));
};
